import { memo, useMemo, useState } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import type { Pin, PinKind, PinSide } from '../types';
import { useStore, selectActiveEdges, type CompNode } from '../store';

const PIN_COLORS: Record<PinKind, string> = {
  power: '#dc2626',
  gnd: '#111827',
  signal: '#2563eb',
  comm: '#7c3aed',
  analog: '#059669',
  pwm: '#d97706',
  other: '#64748b',
};

const PIN_PITCH = 16;
const GROUP_GAP = 18;
const GROUP_HEADER = 14;
const SIDE_PADDING = 14;

const BRACKET_AREA = 28;
const BRACKET_OFFSET = 4;
const BRACKET_THICK = 6;
const LABEL_OFFSET = 14;

type Positioned = {
  pin: Pin;
  group: string;
  order: number;
  /** fraction along the side, 0..1 */
  pos: number;
};

type SideLayout = {
  positioned: Positioned[];
  groups: Array<{ name: string; start: number; end: number; count: number }>;
  length: number;
};

function sideToPosition(side: PinSide): Position {
  switch (side) {
    case 'left':
      return Position.Left;
    case 'right':
      return Position.Right;
    case 'top':
      return Position.Top;
    case 'bottom':
      return Position.Bottom;
  }
}

const SIDE_ORDER: PinSide[] = ['top', 'right', 'bottom', 'left'];

function rotateSide(side: PinSide, rotation: number): PinSide {
  const idx = SIDE_ORDER.indexOf(side);
  const steps = Math.round(((rotation % 360) + 360) % 360 / 90);
  return SIDE_ORDER[(idx + steps) % 4];
}

function layoutSide(pins: Pin[]): SideLayout {
  if (pins.length === 0) {
    return { positioned: [], groups: [], length: 0 };
  }
  type Chunk = { name: string; pins: Pin[] };
  const chunks: Chunk[] = [];
  for (const p of pins) {
    const name = (p.group ?? '').trim();
    const last = chunks[chunks.length - 1];
    if (last && last.name === name) last.pins.push(p);
    else chunks.push({ name, pins: [p] });
  }

  let cursor = SIDE_PADDING;
  const positioned: Positioned[] = [];
  const groups: Array<{ name: string; start: number; end: number; count: number }> = [];
  chunks.forEach((c, ci) => {
    if (ci > 0) cursor += GROUP_GAP;
    if (c.name) cursor += GROUP_HEADER;
    const groupStart = cursor;
    c.pins.forEach((pin, i) => {
      positioned.push({ pin, group: c.name, order: i, pos: cursor });
      cursor += PIN_PITCH;
    });
    const groupEnd = cursor - PIN_PITCH;
    groups.push({ name: c.name, start: groupStart, end: groupEnd, count: c.pins.length });
  });
  cursor += SIDE_PADDING;
  return { positioned, groups, length: cursor };
}

function PinHandle({
  pin,
  posPx,
  side,
  labelInset,
  nodeId,
  editing,
  highlighted,
  onStartEdit,
  onFinishEdit,
}: {
  pin: Pin;
  posPx: number;
  side: PinSide;
  labelInset: number;
  nodeId: string;
  editing: boolean;
  highlighted: boolean;
  onStartEdit: () => void;
  onFinishEdit: (newName: string | null) => void;
}) {
  const styleBase: React.CSSProperties = highlighted
    ? {
        width: 14,
        height: 14,
        background: PIN_COLORS[pin.kind],
        border: '2px solid #2f81f7',
        boxShadow: '0 0 0 3px rgba(47, 129, 247, 0.45)',
        zIndex: 10,
      }
    : {
        width: 10,
        height: 10,
        background: PIN_COLORS[pin.kind],
        border: '2px solid #0f172a',
      };
  const position = sideToPosition(side);
  const positional: React.CSSProperties =
    side === 'left' || side === 'right' ? { top: posPx } : { left: posPx };

  return (
    <>
      <Handle
        type="source"
        position={position}
        id={pin.id}
        style={{ ...styleBase, ...positional }}
        isConnectableStart
        isConnectableEnd
      />
      <Handle
        type="target"
        position={position}
        id={pin.id}
        style={{ ...styleBase, ...positional, opacity: 0 }}
        isConnectableStart
        isConnectableEnd
      />
      <PinLabel
        pin={pin}
        side={side}
        posPx={posPx}
        labelInset={labelInset}
        nodeId={nodeId}
        editing={editing}
        highlighted={highlighted}
        onStartEdit={onStartEdit}
        onFinishEdit={onFinishEdit}
      />
    </>
  );
}

interface PinLabelProps {
  pin: Pin;
  side: PinSide;
  posPx: number;
  labelInset: number;
  nodeId: string;
  editing: boolean;
  highlighted: boolean;
  onStartEdit: () => void;
  onFinishEdit: (newName: string | null) => void;
}

function PinLabel(props: PinLabelProps) {
  const { pin, side, posPx, labelInset, editing, highlighted, onStartEdit, onFinishEdit } = props;

  const inside: React.CSSProperties = {
    color: 'rgba(255,255,255,0.92)',
  };
  const outside: React.CSSProperties = {
    color: '#475569',
  };

  const base: React.CSSProperties = {
    position: 'absolute',
    fontSize: 10,
    whiteSpace: 'nowrap',
    fontFamily: 'ui-monospace, Consolas, monospace',
    lineHeight: 1,
    cursor: 'text',
    pointerEvents: 'auto',
    userSelect: 'none',
  };

  const positional: React.CSSProperties = (() => {
    if (side === 'left') {
      return { left: labelInset, top: posPx, transform: 'translateY(-50%)', marginTop: 5 };
    }
    if (side === 'right') {
      return {
        right: labelInset,
        top: posPx,
        transform: 'translateY(-50%)',
        marginTop: 5,
        textAlign: 'right' as const,
      };
    }
    if (side === 'top') {
      return {
        left: posPx,
        top: -6,
        transform: 'translate(-50%, -100%)',
        writingMode: 'vertical-rl' as const,
        padding: '0 2px',
      };
    }
    return {
      left: posPx,
      bottom: -6,
      transform: 'translate(-50%, 100%)',
      writingMode: 'vertical-rl' as const,
      padding: '0 2px',
    };
  })();

  const colorStyle = side === 'left' || side === 'right' ? inside : outside;

  if (editing) {
    return (
      <input
        autoFocus
        defaultValue={pin.name}
        className="nodrag nopan"
        onFocus={(e) => e.target.select()}
        onBlur={(e) => onFinishEdit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          else if (e.key === 'Escape') onFinishEdit(null);
          e.stopPropagation();
        }}
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          ...base,
          ...positional,
          ...colorStyle,
          background: '#fff',
          color: '#0f172a',
          border: '1px solid #2563eb',
          boxShadow: '0 0 0 2px rgba(37, 99, 235, 0.25)',
          borderRadius: 3,
          padding: '1px 4px',
          outline: 'none',
          minWidth: 36,
          maxWidth: 140,
          // writingMode on an <input> renders oddly; override for top/bottom edits
          writingMode: 'horizontal-tb' as const,
        }}
      />
    );
  }

  const highlightStyle: React.CSSProperties = highlighted
    ? {
        color: '#2f81f7',
        fontWeight: 700,
        textShadow: '0 0 4px rgba(47, 129, 247, 0.5)',
      }
    : {};

  return (
    <div
      onDoubleClick={(e) => {
        e.stopPropagation();
        onStartEdit();
      }}
      title="Double-click to rename"
      style={{
        ...base,
        ...positional,
        ...colorStyle,
        ...highlightStyle,
      }}
    >
      {pin.name}
    </div>
  );
}

function fitFontSize(text: string, availablePx: number, basePx = 10, minPx = 7): number {
  // Approx per-character length when rendered (monospace-ish ~0.6em horizontally, ~1em vertically).
  const perChar = basePx * 0.62;
  const needed = text.length * perChar;
  if (needed <= availablePx) return basePx;
  const scaled = Math.floor((availablePx / text.length) / 0.62);
  return Math.max(minPx, Math.min(basePx, scaled));
}

function GroupBracket({
  side,
  start,
  end,
  name,
}: {
  side: PinSide;
  start: number;
  end: number;
  name: string;
}) {
  if (!name) return null;
  const color = 'rgba(255,255,255,0.85)';
  const thickness = 1;
  const span = end - start + 12;
  const labelFont = fitFontSize(name, span + 14);

  if (side === 'top') {
    // Bracket opens UP toward pins (pins at y<0 relative to this area)
    return (
      <>
        <div
          style={{
            position: 'absolute',
            top: BRACKET_OFFSET,
            left: start - 6,
            width: end - start + 12,
            borderBottom: `${thickness}px solid ${color}`,
            borderLeft: `${thickness}px solid ${color}`,
            borderRight: `${thickness}px solid ${color}`,
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
            height: BRACKET_THICK,
          }}
        />
        <div
          title={name}
          style={{
            position: 'absolute',
            top: LABEL_OFFSET,
            left: (start + end) / 2,
            transform: 'translateX(-50%)',
            fontSize: labelFont,
            color,
            fontWeight: 700,
            letterSpacing: 0.3,
            pointerEvents: 'auto',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </div>
      </>
    );
  }

  if (side === 'bottom') {
    // Bracket opens DOWN toward pins (pins at y>area)
    return (
      <>
        <div
          style={{
            position: 'absolute',
            bottom: BRACKET_OFFSET,
            left: start - 6,
            width: end - start + 12,
            borderTop: `${thickness}px solid ${color}`,
            borderLeft: `${thickness}px solid ${color}`,
            borderRight: `${thickness}px solid ${color}`,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            height: BRACKET_THICK,
          }}
        />
        <div
          title={name}
          style={{
            position: 'absolute',
            bottom: LABEL_OFFSET,
            left: (start + end) / 2,
            transform: 'translateX(-50%)',
            fontSize: labelFont,
            color,
            fontWeight: 700,
            letterSpacing: 0.3,
            pointerEvents: 'auto',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </div>
      </>
    );
  }

  if (side === 'left') {
    // Bracket opens LEFT toward pins (pins at x<area)
    return (
      <>
        <div
          style={{
            position: 'absolute',
            left: BRACKET_OFFSET,
            top: start - 6,
            height: end - start + 12,
            borderRight: `${thickness}px solid ${color}`,
            borderTop: `${thickness}px solid ${color}`,
            borderBottom: `${thickness}px solid ${color}`,
            borderTopRightRadius: 4,
            borderBottomRightRadius: 4,
            width: BRACKET_THICK,
          }}
        />
        <div
          title={name}
          style={{
            position: 'absolute',
            left: LABEL_OFFSET,
            top: (start + end) / 2,
            transform: 'translate(0, -50%)',
            writingMode: 'vertical-rl',
            fontSize: labelFont,
            color,
            fontWeight: 700,
            letterSpacing: 0.3,
            pointerEvents: 'auto',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </div>
      </>
    );
  }
  // right — bracket opens RIGHT toward pins
  return (
    <>
      <div
        style={{
          position: 'absolute',
          right: BRACKET_OFFSET,
          top: start - 6,
          height: end - start + 12,
          borderLeft: `${thickness}px solid ${color}`,
          borderTop: `${thickness}px solid ${color}`,
          borderBottom: `${thickness}px solid ${color}`,
          borderTopLeftRadius: 4,
          borderBottomLeftRadius: 4,
          width: BRACKET_THICK,
        }}
      />
      <div
        title={name}
        style={{
          position: 'absolute',
          right: LABEL_OFFSET,
          top: (start + end) / 2,
          transform: 'translate(0, -50%)',
          writingMode: 'vertical-rl',
          fontSize: labelFont,
          color,
          fontWeight: 700,
          letterSpacing: 0.3,
          pointerEvents: 'auto',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </div>
    </>
  );
}

function ComponentNodeImpl({ id, data, selected }: NodeProps<CompNode>) {
  const updatePin = useStore((s) => s.updatePin);
  const selectedEdgeId = useStore((s) => s.selectedEdgeId);
  const edges = useStore(selectActiveEdges);
  const [editingPinId, setEditingPinId] = useState<string | null>(null);

  const onStartEdit = (pinId: string) => setEditingPinId(pinId);
  const onFinishEdit = (pinId: string, newName: string | null) => {
    if (newName !== null) {
      const trimmed = newName.trim();
      if (trimmed) updatePin(id, pinId, { name: trimmed });
    }
    setEditingPinId(null);
  };

  // When a wire is selected, highlight the pins it connects to on this node.
  const highlightedPinIds = useMemo(() => {
    if (!selectedEdgeId) return new Set<string>();
    const e = edges.find((ed) => ed.id === selectedEdgeId);
    if (!e) return new Set<string>();
    const ids = new Set<string>();
    if (e.source === id && e.sourceHandle) ids.add(e.sourceHandle);
    if (e.target === id && e.targetHandle) ids.add(e.targetHandle);
    return ids;
  }, [selectedEdgeId, edges, id]);

  const rotation = data.rotation ?? 0;
  const effectiveSide = (p: Pin) => rotateSide(p.side, rotation);
  const leftPins = data.pins.filter((p) => effectiveSide(p) === 'left');
  const rightPins = data.pins.filter((p) => effectiveSide(p) === 'right');
  const topPins = data.pins.filter((p) => effectiveSide(p) === 'top');
  const bottomPins = data.pins.filter((p) => effectiveSide(p) === 'bottom');

  const left = layoutSide(leftPins);
  const right = layoutSide(rightPins);
  const top = layoutSide(topPins);
  const bottom = layoutSide(bottomPins);

  const headerH = 38;
  const hasTopGroups = top.groups.some((g) => g.name);
  const hasBottomGroups = bottom.groups.some((g) => g.name);
  const hasLeftGroups = left.groups.some((g) => g.name);
  const hasRightGroups = right.groups.some((g) => g.name);

  const topBracketH = hasTopGroups ? BRACKET_AREA : 0;
  const bottomBracketH = hasBottomGroups ? BRACKET_AREA : 0;
  const leftBracketW = hasLeftGroups ? BRACKET_AREA : 0;
  const rightBracketW = hasRightGroups ? BRACKET_AREA : 0;

  const innerW = Math.max(180, Math.max(top.length, bottom.length));
  const innerH = Math.max(60, Math.max(left.length, right.length));

  const width = leftBracketW + innerW + rightBracketW;
  const height = topBracketH + headerH + innerH + bottomBracketH;

  return (
    <div
      style={{
        width,
        height,
        background: data.color ?? '#334155',
        border: selected
          ? '2px solid #2f81f7'
          : '1px solid rgba(15, 23, 42, 0.18)',
        borderRadius: 'var(--radius)',
        position: 'relative',
        boxShadow: selected
          ? '0 0 0 3px rgba(47, 129, 247, 0.25), 0 10px 24px rgba(15, 23, 42, 0.18)'
          : '0 6px 16px rgba(15, 23, 42, 0.12), 0 1px 2px rgba(15, 23, 42, 0.06)',
        color: 'white',
      }}
    >
      {/* Top bracket area — adjacent to top pins */}
      {topPins.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: leftBracketW,
            width: innerW,
            height: topBracketH,
          }}
        >
          {top.groups.map((g, i) => (
            <GroupBracket key={`t-${i}`} side="top" start={g.start} end={g.end} name={g.name} />
          ))}
        </div>
      )}

      {/* Header (pushed below top bracket area) */}
      <div
        style={{
          position: 'absolute',
          top: topBracketH,
          left: 0,
          right: 0,
          height: headerH,
          boxSizing: 'border-box',
          padding: '6px 10px',
          borderBottom: '1px solid rgba(255,255,255,0.15)',
          fontWeight: 600,
          fontSize: 13,
        }}
      >
        {data.label}
        {data.partNumber && (
          <div style={{ fontSize: 10, opacity: 0.8, fontWeight: 400 }}>{data.partNumber}</div>
        )}
      </div>

      {/* Bottom bracket area — adjacent to bottom pins */}
      {bottomPins.length > 0 && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: leftBracketW,
            width: innerW,
            height: bottomBracketH,
          }}
        >
          {bottom.groups.map((g, i) => (
            <GroupBracket key={`b-${i}`} side="bottom" start={g.start} end={g.end} name={g.name} />
          ))}
        </div>
      )}

      {/* Left bracket area */}
      {leftPins.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: topBracketH + headerH,
            left: 0,
            width: leftBracketW,
            height: innerH,
          }}
        >
          {left.groups.map((g, i) => (
            <GroupBracket key={`l-${i}`} side="left" start={g.start} end={g.end} name={g.name} />
          ))}
        </div>
      )}

      {/* Right bracket area */}
      {rightPins.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: topBracketH + headerH,
            right: 0,
            width: rightBracketW,
            height: innerH,
          }}
        >
          {right.groups.map((g, i) => (
            <GroupBracket key={`r-${i}`} side="right" start={g.start} end={g.end} name={g.name} />
          ))}
        </div>
      )}

      {/* Pins rendered at absolute coords of the node.
          labelInset pushes side labels past their bracket area (so pin labels
          don't overlap the vertical group headings). */}
      {top.positioned.map((pp) => (
        <PinHandle
          key={pp.pin.id}
          pin={pp.pin}
          side="top"
          posPx={leftBracketW + pp.pos}
          labelInset={0}
          nodeId={id}
          editing={editingPinId === pp.pin.id}
          highlighted={highlightedPinIds.has(pp.pin.id)}
          onStartEdit={() => onStartEdit(pp.pin.id)}
          onFinishEdit={(newName) => onFinishEdit(pp.pin.id, newName)}
        />
      ))}
      {bottom.positioned.map((pp) => (
        <PinHandle
          key={pp.pin.id}
          pin={pp.pin}
          side="bottom"
          posPx={leftBracketW + pp.pos}
          labelInset={0}
          nodeId={id}
          editing={editingPinId === pp.pin.id}
          highlighted={highlightedPinIds.has(pp.pin.id)}
          onStartEdit={() => onStartEdit(pp.pin.id)}
          onFinishEdit={(newName) => onFinishEdit(pp.pin.id, newName)}
        />
      ))}
      {left.positioned.map((pp) => (
        <PinHandle
          key={pp.pin.id}
          pin={pp.pin}
          side="left"
          posPx={topBracketH + headerH + pp.pos}
          labelInset={leftBracketW + 12}
          nodeId={id}
          editing={editingPinId === pp.pin.id}
          highlighted={highlightedPinIds.has(pp.pin.id)}
          onStartEdit={() => onStartEdit(pp.pin.id)}
          onFinishEdit={(newName) => onFinishEdit(pp.pin.id, newName)}
        />
      ))}
      {right.positioned.map((pp) => (
        <PinHandle
          key={pp.pin.id}
          pin={pp.pin}
          side="right"
          posPx={topBracketH + headerH + pp.pos}
          labelInset={rightBracketW + 12}
          nodeId={id}
          editing={editingPinId === pp.pin.id}
          highlighted={highlightedPinIds.has(pp.pin.id)}
          onStartEdit={() => onStartEdit(pp.pin.id)}
          onFinishEdit={(newName) => onFinishEdit(pp.pin.id, newName)}
        />
      ))}
    </div>
  );
}

export const ComponentNode = memo(ComponentNodeImpl);
